import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, CheckCircle2, Info, LoaderCircle, X, XCircle } from 'lucide-react';
import { confirmDialog, inferLegacyToast, registerNotificationListeners, toast } from './notificationService';
import './notifications.css';

const NotificationContext = createContext({ toast, confirm: confirmDialog });
const DEFAULT_DURATION = { success: 3500, info: 4500, warning: 5500, error: 0, loading: 0 };
const TITLES = { success: 'Success', error: 'Error', warning: 'Warning', info: 'Information', loading: 'In progress' };
const ICONS = { success: CheckCircle2, error: XCircle, warning: AlertTriangle, info: Info, loading: LoaderCircle };

function ToastItem({ item, onDismiss }) {
  const duration = item.duration ?? DEFAULT_DURATION[item.type] ?? 4500;
  const [leaving, setLeaving] = useState(false);
  const [paused, setPaused] = useState(false);
  const remaining = useRef(duration);
  const startedAt = useRef(Date.now());

  const close = useCallback(() => {
    setLeaving(true);
    window.setTimeout(() => onDismiss(item.id), 220);
  }, [item.id, onDismiss]);

  useEffect(() => {
    if (!duration || paused) return undefined;
    startedAt.current = Date.now();
    const timer = window.setTimeout(close, remaining.current);
    return () => {
      window.clearTimeout(timer);
      remaining.current = Math.max(0, remaining.current - (Date.now() - startedAt.current));
    };
  }, [close, duration, paused]);

  const Icon = ICONS[item.type] || Info;
  return (
    <div
      className={`crm-toast crm-toast--${item.type}${leaving ? ' crm-toast--leaving' : ''}`}
      role={item.type === 'error' ? 'alert' : 'status'}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="crm-toast__icon"><Icon size={21} aria-hidden="true" /></div>
      <div className="crm-toast__content">
        <div className="crm-toast__title">{item.title || TITLES[item.type]}</div>
        <div className="crm-toast__message">{item.message}</div>
      </div>
      <button className="crm-toast__close" type="button" onClick={close} aria-label="Close notification"><X size={17} /></button>
      {!!duration && (
        <div
          className="crm-toast__progress"
          style={{ animationDuration: `${duration}ms`, animationPlayState: paused ? 'paused' : 'running' }}
        />
      )}
    </div>
  );
}

export function NotificationProvider({ children }) {
  const [items, setItems] = useState([]);
  const [confirmation, setConfirmation] = useState(null);

  const remove = useCallback((id) => setItems((current) => current.filter((item) => item.id !== id)), []);

  useEffect(() => registerNotificationListeners(
    (next) => {
      if (next.dismiss) return remove(next.id);
      setItems((current) => {
        const sameId = current.findIndex((item) => item.id === next.id);
        if (sameId >= 0) return [next, ...current.filter((_, index) => index !== sameId)];
        const duplicate = current.some((item) => item.type === next.type && item.message === next.message);
        return duplicate ? current : [next, ...current];
      });
    },
    setConfirmation
  ), [remove]);

  useEffect(() => {
    const nativeAlert = window.alert;
    window.alert = inferLegacyToast;
    return () => { window.alert = nativeAlert; };
  }, []);

  useEffect(() => {
    if (!confirmation) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        confirmation.resolve(false);
        setConfirmation(null);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [confirmation]);

  const finishConfirmation = (answer) => {
    confirmation?.resolve(answer);
    setConfirmation(null);
  };

  return (
    <NotificationContext.Provider value={{ toast, confirm: confirmDialog }}>
      {children}
      {createPortal(
        <>
          <div className="crm-toast-region" aria-live="polite" aria-label="Notifications">
            {items.map((item) => <ToastItem key={`${item.id}-${item.type}-${item.message}`} item={item} onDismiss={remove} />)}
          </div>
          {confirmation && (
            <div className="crm-confirm-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && finishConfirmation(false)}>
              <section className={`crm-confirm crm-confirm--${confirmation.tone}`} role="alertdialog" aria-modal="true" aria-labelledby="crm-confirm-title" aria-describedby="crm-confirm-message">
                <div className="crm-confirm__symbol"><AlertTriangle size={25} /></div>
                <div className="crm-confirm__body">
                  <h2 id="crm-confirm-title">{confirmation.title}</h2>
                  <p id="crm-confirm-message">{confirmation.message}</p>
                </div>
                <div className="crm-confirm__actions">
                  <button type="button" className="crm-confirm__cancel" onClick={() => finishConfirmation(false)}>{confirmation.cancelText}</button>
                  <button type="button" className="crm-confirm__accept" autoFocus onClick={() => finishConfirmation(true)}>{confirmation.confirmText}</button>
                </div>
              </section>
            </div>
          )}
        </>, document.body
      )}
    </NotificationContext.Provider>
  );
}

export const useNotifications = () => useContext(NotificationContext);
